import type { Vec2 } from './types.js';

export interface Landmass {
  name: string;
  polygon: Vec2[];
}

/**
 * Simple polygon coastlines. Coordinates in km from Cádiz, X+ = East, Y+ = South.
 * These are rough sketches — they exist so ships suffer consequences for running aground,
 * and so the at-sea screen can report coast bearings.
 *
 * Key geographic facts baked in:
 *  - Cádiz (0,0) is on the SW tip of the Iberian Peninsula; the open Atlantic is to the west.
 *  - The Strait of Gibraltar is ~80 km EAST of Cádiz, not south. Cape Spartel (Africa's NW tip)
 *    is at roughly (36, 78) — southeast of Cádiz — so ships heading west are in open ocean.
 *  - Africa's Atlantic coast runs SW from Cape Spartel toward Dakar (~1000 km west, 2400 km south).
 *  - Italy is added with Genoa as a starting port; ships must thread the Strait of Gibraltar
 *    before reaching the Atlantic.
 */
export const LANDMASSES: Landmass[] = [
  {
    name: 'Iberian Peninsula',
    polygon: [
      { x: -80,  y: -20  },
      { x: 500,  y: -20  },
      { x: 500,  y: -900 },
      { x: -80,  y: -900 },
    ],
  },
  {
    name: 'France',
    polygon: [
      { x: -200, y: -780  },
      { x: 1100, y: -780  },
      { x: 1100, y: -1100 },
      { x: 300,  y: -1500 },
      { x: -150, y: -1450 },
      { x: -360, y: -1300 },
      { x: -360, y: -1000 },
    ],
  },
  {
    name: 'British Isles',
    polygon: [
      { x: -700, y: -1500 },
      { x: -50,  y: -1500 },
      { x: -50,  y: -1900 },
      { x: -200, y: -2400 },
      { x: -700, y: -2400 },
    ],
  },
  {
    name: 'Northern Europe',
    polygon: [
      { x: 200,  y: -1400 },
      { x: 1100, y: -1400 },
      { x: 1100, y: -2400 },
      { x: 200,  y: -2400 },
    ],
  },
  {
    // Africa's NW tip (Cape Spartel) is at (36, 78) — east and south of Cádiz.
    // Ships from Cádiz heading west or SW are in open Atlantic, not near Africa.
    // The Strait of Gibraltar is the narrow channel at x≈80-90, y≈20-78.
    name: 'Northwest Africa',
    polygon: [
      { x: 36,   y: 78   }, // Cape Spartel — Atlantic side of Strait
      { x: 90,   y: 67   }, // Ceuta — Mediterranean side of Strait
      { x: 850,  y: 5    }, // Algeria (Mediterranean coast)
      { x: 1200, y: 5    }, // Tunisia / Libya
      { x: 1200, y: 3500 }, // southern map extent
      { x: 0,    y: 3500 },
      { x: -665, y: 3000 }, // Guinea area
      { x: -997, y: 2420 }, // Dakar — westernmost point of Africa
      { x: -593, y: 955  }, // Cap Juby (Western Sahara)
      { x: -296, y: 677  }, // Agadir (Morocco)
      { x: -117, y: 322  }, // Casablanca
    ],
  },
  {
    // Simplified Italian boot. Genoa (1290, -880) sits just off the NW Ligurian coast.
    // Ships from Genoa must sail WSW through the Tyrrhenian Sea, then through the
    // Strait of Gibraltar before reaching the open Atlantic.
    name: 'Italian Peninsula',
    polygon: [
      { x: 1200, y: -900 }, // NW corner (Ligurian coast / French border)
      { x: 2300, y: -950 }, // NE corner (Slovenia / Adriatic head)
      { x: 2300, y: -400 }, // Adriatic east coast
      { x: 2150, y: -460 }, // Brindisi (heel)
      { x: 1900, y: -80  }, // Toe (Reggio Calabria)
      { x: 1680, y: -100 }, // Sole going west
      { x: 1580, y: -250 }, // Arch (instep)
      { x: 1750, y: -500 }, // Shin / Naples coast
      { x: 1680, y: -680 }, // Knee (Rome coast)
      { x: 1400, y: -780 }, // Livorno / Ligurian coast
    ],
  },
  {
    name: 'Sicily',
    polygon: [
      { x: 1430, y: -80  }, // NW (Capo San Vito)
      { x: 1870, y: -200 }, // NE (Messina)
      { x: 1750, y: 0    }, // SE (Capo Passero)
      { x: 1430, y: -30  }, // SW closes
    ],
  },
  {
    // North America's Atlantic coast — Florida tip in the south up to Newfoundland.
    // Coastline is the eastern edge of the polygon; we close it far to the west / north
    // (off-map) since the interior continent is irrelevant to gameplay.
    name: 'North America',
    polygon: [
      { x: -7250, y: 1450 }, // Florida tip
      { x: -6960, y: 800  }, // N Florida (St. Augustine)
      { x: -6700, y: 400  }, // Carolinas (Charleston)
      { x: -6230, y: -40  }, // Chesapeake (Norfolk)
      { x: -5860, y: -460 }, // NY harbor
      { x: -5550, y: -640 }, // Cape Cod / Boston
      { x: -5200, y: -800 }, // Maine
      { x: -4830, y: -880 }, // Nova Scotia (Halifax)
      { x: -4400, y: -1100 }, // Cape Breton
      { x: -3700, y: -1280 }, // Newfoundland (St. John's)
      { x: -3500, y: -1700 }, // Labrador edge
      { x: -9500, y: -1700 }, // close west / north (off-map)
      { x: -9500, y: 1700  }, // close south (off-map; Gulf of Mexico unmodeled)
    ],
  },
];

/** Standard ray-casting point-in-polygon. */
export function pointInPolygon(p: Vec2, poly: Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x, yi = poly[i].y;
    const xj = poly[j].x, yj = poly[j].y;
    const intersect = ((yi > p.y) !== (yj > p.y))
      && (p.x < (xj - xi) * (p.y - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Return the first landmass containing p, or null if none. */
export function landmassContaining(p: Vec2): Landmass | null {
  for (const m of LANDMASSES) {
    if (pointInPolygon(p, m.polygon)) return m;
  }
  return null;
}

function distanceToSegment(p: Vec2, a: Vec2, b: Vec2): { dist: number; nearX: number; nearY: number } {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  let t = lengthSq === 0 ? 0 : ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));
  const nearX = a.x + t * dx;
  const nearY = a.y + t * dy;
  return { dist: Math.hypot(p.x - nearX, p.y - nearY), nearX, nearY };
}

/** Compute the minimum distance from p to a polygon edge. */
export function distanceToPolygon(p: Vec2, poly: Vec2[]): number {
  let min = Infinity;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const { dist } = distanceToSegment(p, poly[j], poly[i]);
    if (dist < min) min = dist;
  }
  return min;
}

/**
 * Find the nearest landmass to p (by distance to edge), within maxKm.
 * bearing points from p toward the nearest point on the landmass edge
 * (used by callers to steer away: bearing + π = away from land).
 */
export function nearestLandmass(
  p: Vec2,
  maxKm: number,
): { landmass: Landmass; distanceKm: number; bearing: number } | null {
  let best: { landmass: Landmass; distanceKm: number; bearing: number } | null = null;
  for (const m of LANDMASSES) {
    let minDist = Infinity;
    let bestNearX = 0, bestNearY = 0;
    for (let i = 0, j = m.polygon.length - 1; i < m.polygon.length; j = i++) {
      const { dist, nearX, nearY } = distanceToSegment(p, m.polygon[j], m.polygon[i]);
      if (dist < minDist) { minDist = dist; bestNearX = nearX; bestNearY = nearY; }
    }
    if (minDist > maxKm) continue;
    if (!best || minDist < best.distanceKm) {
      best = {
        landmass: m,
        distanceKm: minDist,
        bearing: Math.atan2(bestNearY - p.y, bestNearX - p.x),
      };
    }
  }
  return best;
}
